import { shimmer } from "@components/skeleton/Skeleton.styles";
import { transparentize } from "polished";
import styled, { css, DefaultTheme } from "styled-components";

import { T_PLAIN_small, T_PLAIN_small_hig, T_TITLE_5 } from "../../global.style";
import { Theme } from "../../theme";
import { ProgressBarColor } from "./ProgressBar.utils";

type ProgressBarColorType = ProgressBarColor | keyof DefaultTheme;

interface IProgressBar {
    $value?: number;
    $color?: ProgressBarColorType;
    $animated?: boolean;
}

const getBackgroundColor = (color: ProgressBarColorType, theme: Theme): string => {
    switch (color) {
        case ProgressBarColor.Semantic:
        case ProgressBarColor.Default:
            return transparentize(0.5, theme.C_ACT_hover_without_opacity);
        case ProgressBarColor.Gray:
            return theme.C_BG_progresswheeel_gray;
        default:
            return transparentize(0.7, theme.C_FIELD_table_line);
    }
};

const getFillBackgroundColor = (color: ProgressBarColorType, theme: Theme): string => {
    switch (color) {
        case ProgressBarColor.Semantic:
            return theme.C_BG_progressbar_semantic;
        case ProgressBarColor.Gray:
            return theme.C_ACT_thick_line;
        case ProgressBarColor.Default:
            return theme.C_BG_progressbar;
        default:
            return theme[color];
    }
};

const getBorderColor = (color: ProgressBarColorType, theme: Theme): string => {
    switch (color) {
        case ProgressBarColor.Semantic:
        case ProgressBarColor.Default:
            return transparentize(0.5, theme.C_ACT_main);
        case ProgressBarColor.Gray:
            return theme.C_ACT_thick_line;
        default:
            return transparentize(0.7, theme.C_ACT_thick_line);
    }
};

export const StyledProgressBar = styled.div<{ $minWidth?: number }>`
    width: auto;
    min-width: ${props => (props.$minWidth ? `${props.$minWidth}px` : "0")};
    display: flex;
    flex-direction: column;
    align-content: center;
    justify-content: center;
`;

export const ProgressWrapper = styled.div<IProgressBar>`
    height: 8px;
    border-radius: ${props => props.theme.borderLargeRadius};

    background-color: ${props => props.$animated ? "transparent" : getBackgroundColor(props.$color, props.theme)};
    border: 1px solid ${props => getBorderColor(props.$color, props.theme)};

    display: flex;
    position: relative;
`;

export const ProgressValue = styled.div.attrs<IProgressBar>((props: IProgressBar) => ({
    style: {
        clipPath: props.$animated ? "none" : `inset(0 ${100 - props.$value}% 0 0)`
    }
}))<IProgressBar>`
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    position: absolute;
    top: -1px;
    left: -1px;
    border-radius: ${props => props.theme.borderLargeRadius};
    background: ${props => getFillBackgroundColor(props.$color, props.theme)};
    
    ${props => props.$animated && css`
        background-image: linear-gradient(
            90deg,
            ${props.theme.C_ACT_hover_without_opacity} 0%,
            ${props.theme.C_ACT_main} 50%,
            ${props.theme.C_ACT_hover_without_opacity} 100%
        );
        background-size: 200% 100%;
        animation: ${shimmer} 2.5s linear infinite;
        z-index: -1;
    `}
`;

export const StyledCanvas = styled.canvas.attrs({ height: "8px" })`
    position: absolute;
    width: calc(100% + 2px);
    top: -1px;
    left: -1px;
    border-radius: ${props => props.theme.borderLargeRadius};
`;

export const Description = styled.div<{ flexDirection: "row" | "column" }>`
    display: flex;
    flex-direction: ${props => props.flexDirection};
    justify-content: center;
    align-items: center;
    margin-top: 5px;
    white-space: pre;

    span {
        ${T_PLAIN_small};

        &:last-child {
            ${T_PLAIN_small_hig};
        }
    }
`;

export const StatusDescriptionFormatterWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 3px;
    ${T_TITLE_5};
    text-transform: none;
`;